import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  FileUp,
  PackageCheck,
  Plus,
  QrCode,
  Search,
  Wrench,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { api, formatApiError } from "@/lib/api";

const emptyAsset = {
  asset_id: "",
  name: "",
  category: "LT",
  purchase_price: "",
  estimated_value: "",
  brand: "",
  model: "",
  serial_number: "",
  warranty_expiration: "",
  location: "",
  condition: "Good",
  notes: "",
};

const emptyAssignment = {
  asset_id: "",
  person_id: "",
  expected_return_date: "",
  acknowledgment_name: "",
  accessories: "",
};

const emptyReturn = {
  asset_id: "",
  condition: "Good",
  notes: "",
  received_by: "",
};

const emptyMaintenance = {
  asset_id: "",
  issue: "",
  vendor: "",
};

const categoryLabels = {
  LT: "Laptop",
  TB: "Tablet",
  TS: "Testing materials",
  TM: "Therapy materials",
  OF: "Office equipment",
};

export default function AssetsPage({ portalMode = false }) {
  const [assets, setAssets] = useState([]);
  const [people, setPeople] = useState([]);
  const [dash, setDash] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [myAssets, setMyAssets] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [asset, setAsset] = useState(emptyAsset);
  const [assignment, setAssignment] = useState(emptyAssignment);
  const [returnForm, setReturnForm] = useState(emptyReturn);
  const [maintenance, setMaintenance] = useState(emptyMaintenance);
  const [receipt, setReceipt] = useState(null);
  const [pendingReceiptAssetId, setPendingReceiptAssetId] = useState("");
  const [selectedQr, setSelectedQr] = useState(null);
  const [scanCode, setScanCode] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [scannerActive, setScannerActive] = useState(false);
  const [activePanel, setActivePanel] = useState("overview");
  const [showReceive, setShowReceive] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);

  async function load(query = "") {
    try {
      const [assetResponse, peopleResponse, dashboardResponse, alertsResponse] = await Promise.all([
        api.get("/assets", { params: { q: query } }),
        api.get("/assets/people/list"),
        api.get("/assets/dashboard"),
        api.get("/assets/alerts"),
      ]);
      setAssets(assetResponse.data);
      setPeople(peopleResponse.data);
      setDash(dashboardResponse.data);
      setAlerts(alertsResponse.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function loadMyAssets() {
    try {
      const response = await api.get("/assets/my-assigned");
      setMyAssets(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  useEffect(() => {
    if (portalMode) {
      loadMyAssets();
      return;
    }
    load();
  }, [portalMode]);

  useEffect(() => {
    return () => stopScanner();
  }, []);

  function setMessage(message) {
    setError("");
    setSuccess(message);
  }

  async function createAsset(event) {
    event.preventDefault();
    try {
      const response = await api.post("/assets", {
        ...asset,
        purchase_price: Number(asset.purchase_price || 0),
        estimated_value: Number(asset.estimated_value || 0),
        warranty_expiration: asset.warranty_expiration || null,
      });
      if (receipt) {
        try {
          await attachReceipt(response.data.id);
        } catch (receiptError) {
          setAsset(emptyAsset);
          setPendingReceiptAssetId(response.data.id);
          setError("");
          setSuccess(
            "Asset received, but the receipt upload did not finish. Keep the file selected and retry below.",
          );
          await load(search);
          return;
        }
      }
      setAsset(emptyAsset);
      setReceipt(null);
      setPendingReceiptAssetId("");
      setMessage(
        receipt
          ? "Asset received and receipt attached."
          : "Asset received and ready for assignment.",
      );
      await load(search);
    } catch (requestError) {
      setSuccess("");
      setError(formatApiError(requestError));
    }
  }

  async function attachReceipt(assetId) {
    if (!receipt) {
      throw new Error("Choose a receipt file before uploading.");
    }
    const formData = new FormData();
    formData.append("receipt", receipt);
    await api.post(`/assets/${assetId}/receipts`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async function retryReceiptUpload() {
    try {
      await attachReceipt(pendingReceiptAssetId);
      setReceipt(null);
      setPendingReceiptAssetId("");
      setMessage("Receipt attached to the received asset.");
      await load(search);
    } catch (requestError) {
      setSuccess("");
      setError(formatApiError(requestError));
    }
  }

  async function assignAsset(event) {
    event.preventDefault();
    try {
      await api.post(`/assets/${assignment.asset_id}/assign`, {
        person_id: assignment.person_id,
        expected_return_date: assignment.expected_return_date || null,
        acknowledgment_name: assignment.acknowledgment_name,
        accessories: assignment.accessories
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      });
      setAssignment(emptyAssignment);
      setMessage("Asset assignment recorded with acknowledgment.");
      await load(search);
    } catch (requestError) {
      setSuccess("");
      setError(formatApiError(requestError));
    }
  }

  async function returnAsset(event) {
    event.preventDefault();
    try {
      await api.post(`/assets/${returnForm.asset_id}/return`, returnForm);
      setReturnForm(emptyReturn);
      setMessage("Asset return has been recorded and inventory is available again.");
      await load(search);
    } catch (requestError) {
      setSuccess("");
      setError(formatApiError(requestError));
    }
  }

  async function openMaintenance(event) {
    event.preventDefault();
    try {
      await api.post(`/assets/${maintenance.asset_id}/maintenance`, {
        issue: maintenance.issue,
        vendor: maintenance.vendor,
      });
      setMaintenance(emptyMaintenance);
      setMessage("Maintenance case opened and asset moved to In Repair.");
      await load(search);
    } catch (requestError) {
      setSuccess("");
      setError(formatApiError(requestError));
    }
  }

  async function completeMaintenance(assetId) {
    try {
      await api.post(`/assets/${assetId}/maintenance/complete`, {
        resolution: "Maintenance completed and asset verified.",
        condition: "Good",
      });
      setMessage("Maintenance resolved and asset returned to available inventory.");
      await load(search);
    } catch (requestError) {
      setSuccess("");
      setError(formatApiError(requestError));
    }
  }

  async function resolveCode(code) {
    const cleanCode = code.trim();
    if (!cleanCode) {
      return;
    }
    try {
      const response = await api.get("/assets/lookup", { params: { asset_code: cleanCode } });
      setScanResult(response.data);
      setMessage(`QR code matched ${response.data.asset_id}.`);
    } catch (requestError) {
      setSuccess("");
      setScanResult(null);
      setError(formatApiError(requestError));
    }
  }

  async function scanAsset(event) {
    event.preventDefault();
    await resolveCode(scanCode);
  }

  function stopScanner() {
    if (scanTimerRef.current) {
      window.clearInterval(scanTimerRef.current);
      scanTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setScannerActive(false);
  }

  async function startScanner() {
    if (!window.BarcodeDetector || !navigator.mediaDevices?.getUserMedia) {
      setError(
        "Camera QR scanning is not available in this browser. Scan into the Asset ID field instead.",
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      setScannerActive(true);
      scanTimerRef.current = window.setInterval(async () => {
        if (!videoRef.current) {
          return;
        }
        const codes = await detector.detect(videoRef.current);
        if (codes.length && codes[0].rawValue) {
          const detectedCode = codes[0].rawValue;
          setScanCode(detectedCode);
          stopScanner();
          await resolveCode(detectedCode);
        }
      }, 600);
    } catch (requestError) {
      setError(
        "Camera access was not granted. You can still scan using a handheld reader or enter the Asset ID.",
      );
      stopScanner();
    }
  }

  if (portalMode) {
    return (
      <div className="app-shell" data-testid="my-assets-shell">
        <AppSidebar activeItem="My Assets" />
        <div className="app-content">
          <AppHeader />
          <main className="dashboard-page" data-testid="my-assets-page">
            <div className="page-intro">
              <div>
                <p className="eyebrow">EMPLOYEE PORTAL</p>
                <h1>My Assigned Assets</h1>
                <p className="page-subtitle">
                  Your current TAG equipment, return dates, and included accessories.
                </p>
              </div>
            </div>
            {error && (
              <p className="form-message error-message" data-testid="my-assets-error">
                {error}
              </p>
            )}
            {myAssets.length === 0 ? (
              <section className="asset-empty-state" data-testid="my-assets-empty-state">
                <PackageCheck size={28} aria-hidden="true" />
                <h2>No equipment is assigned to you</h2>
                <p>
                  When an operations team member assigns equipment to your directory profile, it
                  will appear here.
                </p>
              </section>
            ) : (
              <section className="asset-card-grid" data-testid="my-assets-list">
                {myAssets.map((item) => (
                  <article
                    className="asset-summary-card"
                    key={item.id}
                    data-testid={`my-asset-${item.id}`}
                  >
                    <QrCode size={20} aria-hidden="true" />
                    <p className="eyebrow">{item.asset_id}</p>
                    <h2>{item.name}</h2>
                    <dl>
                      <div>
                        <dt>Return by</dt>
                        <dd>{item.expected_return_date || "No date set"}</dd>
                      </div>
                      <div>
                        <dt>Accessories</dt>
                        <dd>{item.accessories?.join(", ") || "None listed"}</dd>
                      </div>
                      <div>
                        <dt>Condition</dt>
                        <dd>{item.condition}</dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </section>
            )}
          </main>
        </div>
      </div>
    );
  }

  const assignedAssets = assets.filter((item) => item.status === "Assigned");
  const availableAssets = assets.filter((item) => item.status === "Available");
  const repairAssets = assets.filter((item) => item.status === "In Repair");
  const categorySummary = Object.entries(categoryLabels).map(([key, label]) => ({
    label,
    count: assets.filter((item) => item.category === key).length,
  }));
  const locationSummary = Object.entries(
    assets.reduce(
      (result, item) => ({
        ...result,
        [item.location || "Unassigned"]: (result[item.location || "Unassigned"] || 0) + 1,
      }),
      {},
    ),
  ).map(([label, count]) => ({ label, count }));

  return (
    <div className="app-shell" data-testid="assets-shell">
      <AppSidebar activeItem="Asset Management" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page" data-testid="assets-page">
          <div className="page-intro asset-page-intro">
            <div>
              <p className="eyebrow">INTERNAL OPERATIONS</p>
              <h1>Asset Management</h1>
              <p className="page-subtitle">
                Receive, assign, verify, return, and maintain TAG-owned equipment and materials.
              </p>
            </div>
            <div className="asset-page-actions">
              <button
                className="button button-secondary"
                data-testid="scan-asset-action-button"
                type="button"
                onClick={() => setActivePanel("audit")}
              >
                <QrCode size={16} /> Scan asset
              </button>
              <button
                className="button button-primary"
                data-testid="add-new-asset-button"
                type="button"
                onClick={() => {
                  setActivePanel("assets");
                  setShowReceive(true);
                }}
              >
                <Plus size={16} /> Add new asset
              </button>
            </div>
          </div>

          {error && (
            <p className="form-message error-message" data-testid="asset-error-message">
              {error}
            </p>
          )}
          {success && (
            <p className="form-message success-message" data-testid="asset-success-message">
              {success}
            </p>
          )}
          {pendingReceiptAssetId && (
            <div className="asset-receipt-retry" data-testid="asset-receipt-retry-panel">
              <span>Receipt upload is pending for the newly received asset.</span>
              <button
                className="button button-secondary"
                onClick={retryReceiptUpload}
                data-testid="asset-receipt-retry-button"
                type="button"
              >
                Retry receipt upload
              </button>
            </div>
          )}

          {dash && (
            <section className="report-metric-grid" data-testid="asset-dashboard-metrics">
              <article data-testid="asset-total-metric">
                <small>Total assets</small>
                <strong>{dash.total_assets}</strong>
              </article>
              <article data-testid="asset-value-metric">
                <small>Inventory value</small>
                <strong>${dash.total_value.toLocaleString()}</strong>
              </article>
              <article data-testid="asset-estimated-value-metric">
                <small>Estimated value</small>
                <strong>${dash.estimated_value.toLocaleString()}</strong>
              </article>
              <article data-testid="asset-assigned-metric">
                <small>Assigned</small>
                <strong>{dash.statuses.Assigned}</strong>
              </article>
              <article data-testid="asset-available-metric">
                <small>Available assets</small>
                <strong>{availableAssets.length}</strong>
              </article>
              <article data-testid="asset-maintenance-metric">
                <small>In maintenance</small>
                <strong>{repairAssets.length}</strong>
              </article>
            </section>
          )}

          <div
            className="asset-section-tabs"
            role="tablist"
            aria-label="Asset management workspaces"
          >
            <button
              className={activePanel === "overview" ? "asset-tab-active" : ""}
              onClick={() => setActivePanel("overview")}
              type="button"
              data-testid="asset-overview-tab"
            >
              Overview
            </button>
            <button
              className={activePanel === "assets" ? "asset-tab-active" : ""}
              onClick={() => setActivePanel("assets")}
              type="button"
              data-testid="asset-inventory-tab"
            >
              Assets
            </button>
            <button
              className={activePanel === "lifecycle" ? "asset-tab-active" : ""}
              onClick={() => setActivePanel("lifecycle")}
              type="button"
              data-testid="asset-lifecycle-tab"
            >
              Assignments & returns
            </button>
            <button
              className={activePanel === "maintenance" ? "asset-tab-active" : ""}
              onClick={() => setActivePanel("maintenance")}
              type="button"
              data-testid="asset-maintenance-tab"
            >
              Maintenance
            </button>
            <button
              className={activePanel === "audit" ? "asset-tab-active" : ""}
              onClick={() => setActivePanel("audit")}
              type="button"
              data-testid="asset-audit-tab"
            >
              Inventory audit
            </button>
            <button
              className={activePanel === "reports" ? "asset-tab-active" : ""}
              onClick={() => setActivePanel("reports")}
              type="button"
              data-testid="asset-reports-tab"
            >
              Reports
            </button>
          </div>

          {activePanel === "overview" && (
            <AssetOverview
              categories={categorySummary}
              locations={locationSummary}
              assets={assets}
            />
          )}

          {activePanel === "assets" && (
            <>
              {showReceive && (
                <section className="asset-workflows-grid">
                  <form
                    className="request-form-panel request-form-grid"
                    onSubmit={createAsset}
                    data-testid="receive-asset-form"
                  >
                    <h2 className="form-full">Receive new asset</h2>
                    <label>
                      Pre-printed Asset ID
                      <input
                        data-testid="asset-id-input"
                        value={asset.asset_id}
                        onChange={(event) => setAsset({ ...asset, asset_id: event.target.value })}
                        required
                      />
                    </label>
                    <label>
                      Asset name
                      <input
                        data-testid="asset-name-input"
                        value={asset.name}
                        onChange={(event) => setAsset({ ...asset, name: event.target.value })}
                        required
                      />
                    </label>
                    <label>
                      Category
                      <select
                        data-testid="asset-category-select"
                        value={asset.category}
                        onChange={(event) => setAsset({ ...asset, category: event.target.value })}
                      >
                        {Object.entries(categoryLabels).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Condition
                      <select
                        data-testid="asset-condition-select"
                        value={asset.condition}
                        onChange={(event) => setAsset({ ...asset, condition: event.target.value })}
                      >
                        <option>New</option>
                        <option>Good</option>
                        <option>Fair</option>
                        <option>Damaged</option>
                      </select>
                    </label>
                    <label>
                      Purchase price
                      <input
                        data-testid="asset-purchase-price-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={asset.purchase_price}
                        onChange={(event) =>
                          setAsset({ ...asset, purchase_price: event.target.value })
                        }
                      />
                    </label>
                    <label>
                      Estimated value
                      <input
                        data-testid="asset-estimated-value-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={asset.estimated_value}
                        onChange={(event) =>
                          setAsset({ ...asset, estimated_value: event.target.value })
                        }
                      />
                    </label>
                    <label>
                      Brand
                      <input
                        data-testid="asset-brand-input"
                        value={asset.brand}
                        onChange={(event) => setAsset({ ...asset, brand: event.target.value })}
                      />
                    </label>
                    <label>
                      Model
                      <input
                        data-testid="asset-model-input"
                        value={asset.model}
                        onChange={(event) => setAsset({ ...asset, model: event.target.value })}
                      />
                    </label>
                    <label>
                      Serial number
                      <input
                        data-testid="asset-serial-number-input"
                        value={asset.serial_number}
                        onChange={(event) =>
                          setAsset({ ...asset, serial_number: event.target.value })
                        }
                      />
                    </label>
                    <label>
                      Warranty expiration
                      <input
                        data-testid="asset-warranty-input"
                        type="date"
                        value={asset.warranty_expiration}
                        onChange={(event) =>
                          setAsset({ ...asset, warranty_expiration: event.target.value })
                        }
                      />
                    </label>
                    <label>
                      Storage location
                      <input
                        data-testid="asset-location-input"
                        value={asset.location}
                        onChange={(event) => setAsset({ ...asset, location: event.target.value })}
                      />
                    </label>
                    <label>
                      Receipt / proof of purchase
                      <input
                        data-testid="asset-receipt-input"
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/webp"
                        onChange={(event) => setReceipt(event.target.files?.[0] || null)}
                      />
                    </label>
                    <label className="form-full">
                      Receiving notes
                      <textarea
                        data-testid="asset-notes-input"
                        rows="3"
                        value={asset.notes}
                        onChange={(event) => setAsset({ ...asset, notes: event.target.value })}
                      />
                    </label>
                    <button className="button button-primary" data-testid="create-asset-button">
                      <FileUp size={16} /> Receive asset
                    </button>
                  </form>
                </section>
              )}

              <InventoryTable
                assets={assets}
                search={search}
                setSearch={setSearch}
                load={load}
                setSelectedQr={setSelectedQr}
              />
            </>
          )}

          {activePanel === "reports" && (
            <InventoryTable
              assets={assets}
              search={search}
              setSearch={setSearch}
              load={load}
              setSelectedQr={setSelectedQr}
            />
          )}

          {activePanel === "lifecycle" && (
            <section className="asset-workflows-grid">
              <form
                className="request-form-panel request-form-grid"
                onSubmit={assignAsset}
                data-testid="assign-asset-form"
              >
                <h2 className="form-full">Assign asset</h2>
                <label className="form-full">
                  Asset
                  <select
                    data-testid="assignment-asset-select"
                    value={assignment.asset_id}
                    onChange={(event) =>
                      setAssignment({ ...assignment, asset_id: event.target.value })
                    }
                    required
                  >
                    <option value="">Select available asset</option>
                    {availableAssets.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.asset_id} · {item.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Person
                  <select
                    data-testid="assignment-person-select"
                    value={assignment.person_id}
                    onChange={(event) =>
                      setAssignment({ ...assignment, person_id: event.target.value })
                    }
                    required
                  >
                    <option value="">Select person</option>
                    {people.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.first_name} {item.last_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Expected return
                  <input
                    data-testid="assignment-return-date"
                    type="date"
                    value={assignment.expected_return_date}
                    onChange={(event) =>
                      setAssignment({ ...assignment, expected_return_date: event.target.value })
                    }
                  />
                </label>
                <label className="form-full">
                  Accessories (comma separated)
                  <input
                    data-testid="assignment-accessories-input"
                    value={assignment.accessories}
                    onChange={(event) =>
                      setAssignment({ ...assignment, accessories: event.target.value })
                    }
                  />
                </label>
                <label className="form-full">
                  Acknowledgment name
                  <input
                    data-testid="assignment-acknowledgment-input"
                    value={assignment.acknowledgment_name}
                    onChange={(event) =>
                      setAssignment({ ...assignment, acknowledgment_name: event.target.value })
                    }
                    required
                  />
                </label>
                <button className="button button-primary" data-testid="assignment-submit-button">
                  <ClipboardCheck size={16} /> Assign asset
                </button>
              </form>

              <form
                className="request-form-panel request-form-grid"
                onSubmit={returnAsset}
                data-testid="return-asset-form"
              >
                <h2 className="form-full">Receive returned asset</h2>
                <label className="form-full">
                  Assigned asset
                  <select
                    data-testid="return-asset-select"
                    value={returnForm.asset_id}
                    onChange={(event) =>
                      setReturnForm({ ...returnForm, asset_id: event.target.value })
                    }
                    required
                  >
                    <option value="">Select assigned asset</option>
                    {assignedAssets.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.asset_id} · {item.name} — {item.assigned_person_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Return condition
                  <select
                    data-testid="return-condition-select"
                    value={returnForm.condition}
                    onChange={(event) =>
                      setReturnForm({ ...returnForm, condition: event.target.value })
                    }
                  >
                    <option>New</option>
                    <option>Good</option>
                    <option>Fair</option>
                    <option>Damaged</option>
                  </select>
                </label>
                <label>
                  Received by
                  <input
                    data-testid="return-received-by-input"
                    value={returnForm.received_by}
                    onChange={(event) =>
                      setReturnForm({ ...returnForm, received_by: event.target.value })
                    }
                    required
                  />
                </label>
                <label className="form-full">
                  Return notes
                  <textarea
                    data-testid="return-notes-input"
                    rows="3"
                    value={returnForm.notes}
                    onChange={(event) =>
                      setReturnForm({ ...returnForm, notes: event.target.value })
                    }
                  />
                </label>
                <button className="button button-primary" data-testid="return-submit-button">
                  <PackageCheck size={16} /> Record return
                </button>
              </form>
            </section>
          )}

          {(activePanel === "audit" || activePanel === "maintenance") && (
            <>
                <section className="asset-workflows-grid">
                  <form
                    className="request-form-panel request-form-grid"
                    onSubmit={scanAsset}
                    data-testid="asset-qr-scan-form"
                  >
                    <h2 className="form-full">QR code lookup</h2>
                    <p className="asset-helper form-full">
                      Scan a TAGForce QR label with a handheld reader, type its Asset ID, or use a
                      supported device camera.
                    </p>
                    <label className="form-full">
                      Asset ID / QR code
                      <input
                        data-testid="asset-qr-code-input"
                        value={scanCode}
                        onChange={(event) => setScanCode(event.target.value)}
                        required
                      />
                    </label>
                    <div className="asset-action-row form-full">
                      <button
                        className="button button-primary"
                        data-testid="asset-qr-lookup-button"
                      >
                        <Search size={16} /> Look up code
                      </button>
                      <button
                        className="button button-secondary"
                        onClick={scannerActive ? stopScanner : startScanner}
                        type="button"
                        data-testid="asset-camera-scan-button"
                      >
                        <Camera size={16} /> {scannerActive ? "Stop camera" : "Use camera"}
                      </button>
                    </div>
                    {scannerActive && (
                      <video
                        className="asset-camera-preview form-full"
                        ref={videoRef}
                        muted
                        playsInline
                        data-testid="asset-camera-preview"
                      />
                    )}
                    {scanResult && (
                      <div
                        className="asset-scan-result form-full"
                        data-testid="asset-qr-scan-result"
                      >
                        <CheckCircle2 size={18} />
                        <span>
                          <strong>
                            {scanResult.asset_id} · {scanResult.name}
                          </strong>
                          <small>
                            {scanResult.status} ·{" "}
                            {scanResult.assigned_person_name || "Not assigned"}
                          </small>
                        </span>
                      </div>
                    )}
                  </form>

                  <form
                    className="request-form-panel request-form-grid"
                    onSubmit={openMaintenance}
                    data-testid="maintenance-form"
                  >
                    <h2 className="form-full">Open maintenance case</h2>
                    <label className="form-full">
                      Available asset
                      <select
                        data-testid="maintenance-asset-select"
                        value={maintenance.asset_id}
                        onChange={(event) =>
                          setMaintenance({ ...maintenance, asset_id: event.target.value })
                        }
                        required
                      >
                        <option value="">Select asset</option>
                        {availableAssets.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.asset_id} · {item.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="form-full">
                      Issue
                      <textarea
                        data-testid="maintenance-issue-input"
                        rows="3"
                        value={maintenance.issue}
                        onChange={(event) =>
                          setMaintenance({ ...maintenance, issue: event.target.value })
                        }
                        required
                      />
                    </label>
                    <label>
                      Service vendor
                      <input
                        data-testid="maintenance-vendor-input"
                        value={maintenance.vendor}
                        onChange={(event) =>
                          setMaintenance({ ...maintenance, vendor: event.target.value })
                        }
                      />
                    </label>
                    <button
                      className="button button-primary"
                      data-testid="maintenance-submit-button"
                    >
                      <Wrench size={16} /> Open maintenance
                    </button>
                  </form>
                </section>

                <section className="asset-alert-grid">
                  <div className="requests-table-panel">
                    <div className="section-heading">
                      <div>
                        <h2>Maintenance queue</h2>
                        <p>{repairAssets.length} assets currently in repair</p>
                      </div>
                    </div>
                    {repairAssets.length ? (
                      repairAssets.map((item) => (
                        <div
                          className="asset-queue-row"
                          key={item.id}
                          data-testid={`maintenance-row-${item.id}`}
                        >
                          <span>
                            <strong>
                              {item.asset_id} · {item.name}
                            </strong>
                            <small>Maintenance is open</small>
                          </span>
                          <button
                            className="button button-secondary"
                            onClick={() => completeMaintenance(item.id)}
                            data-testid={`complete-maintenance-${item.id}`}
                            type="button"
                          >
                            Complete
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="asset-helper" data-testid="maintenance-empty-state">
                        No open maintenance cases.
                      </p>
                    )}
                  </div>
                  <div className="requests-table-panel">
                    <div className="section-heading">
                      <div>
                        <h2>Alerts</h2>
                        <p>{alerts.length} asset alerts needing attention</p>
                      </div>
                    </div>
                    {alerts.length ? (
                      alerts.map((alert) => (
                        <div
                          className="asset-alert-row"
                          key={alert.id}
                          data-testid={`asset-alert-${alert.id}`}
                        >
                          <AlertTriangle size={18} aria-hidden="true" />
                          <span>
                            <strong>
                              {alert.asset_code} · {alert.type}
                            </strong>
                            <small>{alert.message}</small>
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="asset-helper" data-testid="asset-alerts-empty-state">
                        No warranty, return, or maintenance alerts.
                      </p>
                    )}
                  </div>
                </section>
              </>
            )}

          {selectedQr && <QrModal asset={selectedQr} onClose={() => setSelectedQr(null)} />}
        </main>
      </div>
    </div>
  );
}

function AssetOverview({ assets, categories, locations }) {
  const total = Math.max(assets.length, 1);
  const recent = [...assets].slice(0, 5);
  return (
    <section className="asset-overview-grid" data-testid="asset-overview">
      <article className="asset-overview-card">
        <div className="section-heading">
          <div>
            <h2>Assets by category</h2>
            <p>Inventory distribution</p>
          </div>
        </div>
        <div className="asset-category-list">
          {categories.map((item) => (
            <div key={item.label}>
              <span>{item.label}</span>
              <div>
                <i style={{ width: `${(item.count / total) * 100}%` }} />
              </div>
              <b>{item.count}</b>
            </div>
          ))}
        </div>
      </article>
      <article className="asset-overview-card">
        <div className="section-heading">
          <div>
            <h2>Assets by location</h2>
            <p>Where TAG equipment is held</p>
          </div>
        </div>
        <div className="asset-category-list">
          {locations.slice(0, 6).map((item) => (
            <div key={item.label}>
              <span>{item.label}</span>
              <div>
                <i style={{ width: `${(item.count / total) * 100}%` }} />
              </div>
              <b>{item.count}</b>
            </div>
          ))}
          {!locations.length && <p className="asset-helper">No locations recorded yet.</p>}
        </div>
      </article>
      <article className="asset-overview-card asset-recent-activity">
        <div className="section-heading">
          <div>
            <h2>Recent inventory activity</h2>
            <p>Latest asset state changes</p>
          </div>
        </div>
        {recent.map((item) => (
          <div className="asset-activity-item" key={item.id}>
            <QrCode size={15} />
            <span>
              <b>
                {item.asset_id} · {item.name}
              </b>
              <small>
                {item.status} · {item.location || "Location pending"}
              </small>
            </span>
          </div>
        ))}
        {!recent.length && (
          <p className="asset-helper">
            Recent activity will appear as assets are received, assigned, and returned.
          </p>
        )}
      </article>
    </section>
  );
}

function InventoryTable({ assets, search, setSearch, load, setSelectedQr }) {
  return (
    <section className="requests-table-panel" data-testid="asset-inventory-panel">
      <div className="section-heading">
        <div>
          <h2>Asset inventory</h2>
          <p>{assets.length} tracked assets</p>
        </div>
        <form
          className="header-search"
          onSubmit={(event) => {
            event.preventDefault();
            load(search);
          }}
        >
          <Search size={16} aria-hidden="true" />
          <input
            data-testid="asset-search-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search Asset ID, name, or serial"
          />
          <button
            className="asset-search-button"
            data-testid="asset-search-submit-button"
            type="submit"
          >
            Search
          </button>
        </form>
      </div>
      <div className="request-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Asset ID</th>
              <th>Name</th>
              <th>Category</th>
              <th>Status</th>
              <th>Location</th>
              <th>Assigned to</th>
              <th>Receipt</th>
              <th>QR</th>
            </tr>
          </thead>
          <tbody>
            {assets.map((item) => (
              <tr key={item.id} data-testid={`asset-row-${item.id}`}>
                <td>
                  <strong>{item.asset_id}</strong>
                </td>
                <td>{item.name}</td>
                <td>{categoryLabels[item.category] || item.category}</td>
                <td>
                  <span
                    className={`asset-status asset-status-${item.status.toLowerCase().replaceAll(" ", "-")}`}
                  >
                    {item.status}
                  </span>
                </td>
                <td>{item.location || "—"}</td>
                <td>{item.assigned_person_name || "Available"}</td>
                <td>
                  {item.receipt_count
                    ? `${item.receipt_count} file${item.receipt_count === 1 ? "" : "s"}`
                    : "—"}
                </td>
                <td>
                  <button
                    className="asset-qr-button"
                    onClick={() => setSelectedQr(item)}
                    data-testid={`asset-qr-button-${item.id}`}
                    type="button"
                    aria-label={`Show QR code for ${item.asset_id}`}
                  >
                    <QrCode size={18} />
                  </button>
                </td>
              </tr>
            ))}
            {!assets.length && (
              <tr>
                <td
                  className="empty-requests"
                  colSpan="8"
                  data-testid="asset-inventory-empty-state"
                >
                  No assets match this search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function QrModal({ asset, onClose }) {
  return (
    <div
      className="asset-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="asset-qr-modal-title"
      data-testid="asset-qr-modal"
    >
      <section className="asset-qr-modal-card">
        <button
          className="asset-modal-close"
          onClick={onClose}
          data-testid="asset-qr-modal-close-button"
          type="button"
          aria-label="Close QR code"
        >
          ×
        </button>
        <p className="eyebrow">TAGFORCE ASSET LABEL</p>
        <h2 id="asset-qr-modal-title">{asset.asset_id}</h2>
        <p>{asset.name}</p>
        <div className="asset-qr-code" data-testid="asset-qr-code">
          <QRCodeSVG value={asset.asset_id} size={220} includeMargin />
        </div>
        <small>Scan this label to open the asset lookup workflow.</small>
      </section>
    </div>
  );
}
